import { FeesRepo } from '@services/fees.repo';
import * as _ from 'lodash';
import * as moment from 'moment';
import { FeeCalculationType } from '@common/enums/FeeCalculation'
import { extractErrorMessage } from '@common/utils/errorUtils';

class FinalRunController {
  // Shared state from parent (bindings)
  public fromDate: Date;
  public toDate: Date;
  public displayMCB: boolean;
  public threshold: number;
  public midRateDate: Date;
  public midRates: Array<any>;
  public currentDate: Date;
  public lastTwelveMonths: Date;
  public reloadFinalParameters: boolean;
  public onDeclineComplete: () => void;

  // Local state
  public selectedCurrency = "MUR";
  public clientFeeParameters;
  public summary: Array<any> = [];
  public totalEquivalentInMUR: number = 0;

  public selectAll: boolean = false;
  public isLoadingParameters: boolean = false;
  public isSavingParameters: boolean = false;
  public hasSearched: boolean = false;
  /** IDs of fee instructions currently being declined (show loader on their button). */
  public decliningInstructionIds: Record<string, boolean> = {};
  public isBulkDeclining: boolean = false;

  // stats
  public totalNav: number = 0;
  public totalFeeValue: number = 0;
  public totalPortfolios: number = 0;

  /** @ngInject */
  constructor(
    private feesRepo: FeesRepo,
    private notifyjs,
    private $mdDialog: angular.material.IDialogService,
    private $rootScope: angular.IRootScopeService,
    private $window: angular.IWindowService
  ) {}

  $onInit() {
    // Initialize mid rates if midRateDate is set
    if (this.midRateDate) {
      this.getMidRateForMonth();
    }
  }

  $onChanges(changes: any) {
    if (changes.reloadFinalParameters && !changes.reloadFinalParameters.isFirstChange()) {
      this.getFinalRunParameters();
    }
    // Fetch mid rates when midRateDate changes
    if (changes.midRateDate && this.midRateDate) {
      this.getMidRateForMonth();
    }
  }


  refreshData = () => {
    this.getFinalRunParameters();
  };

  getFinalRunParameters = () => {
    if (!this.fromDate || !this.toDate) {
      return;
    }

    let feeCalculationType = this.displayMCB ? FeeCalculationType.MCB : FeeCalculationType.EDR;
    this.isLoadingParameters = true;
    this.hasSearched = true;
    let fromDate = moment(this.fromDate).endOf('month').format('YYYY-MM-DD');
    let toDate = moment(this.toDate).endOf('month').format('YYYY-MM-DD');
    this.feesRepo.getFinalRunParameters(fromDate, toDate, feeCalculationType, this.threshold)
      .then(res => {
        this.clientFeeParameters = [];
        const responseData = res.data as any;
        // Handle new response structure with parameters and summary (camelCase from API)
        if (responseData && responseData.parameters) {
          this.clientFeeParameters = responseData.parameters as Array<any>;
          this.summary = responseData.summary || [];
          this.totalEquivalentInMUR = this.summary.reduce((sum, item) => sum + (item.equivalentInMUR || 0), 0);
        } else {
          // Fallback for old response structure (direct array)
          this.clientFeeParameters = responseData as Array<any>;
          this.summary = [];
        }

        this.clientFeeParameters.forEach(client => {
          client.isSelected = this.selectAll;
          client.portfolioFees.forEach(fee => {
            fee.portfolios.forEach(portfolio => {
              if (portfolio) {
                portfolio.isSelected = this.selectAll;
              }
            })
          })
        });

        this.setTotalNav();
        this.setTotalFeeValue();
        this.setTotalPortfolios();
      })
      .catch(err => {
        this.notifyjs("Failed to load fee parameters " + extractErrorMessage(err))
      })
      .finally(() => { this.isLoadingParameters = false; })
  }



  selectAllClients = () => {
    this.clientFeeParameters.forEach(client => {
      client.isSelected = this.selectAll;
      client.portfolioFees.forEach(fee => {
        fee.portfolios.forEach(portfolio => {
          portfolio.isSelected = this.selectAll;
        })
      })
    });
  }

  getPercentageFromDecimal = (value) => {
    return +(value * 100).toFixed(2)
  }

  getPortfolioSelectedText = (client) => {
    let totalPortfoliosSelected = client.portfolioFees.reduce((acc, pf) => {
      return acc + pf.portfolios.filter(p => p && p.isSelected).length
    }, 0);

    let totalPortfolios = client.portfolioFees.reduce((acc, pf) => {
      return acc + pf.portfolios.length
    }, 0);

    if (totalPortfolios == totalPortfoliosSelected) {
      return "All portfolio(s)"
    } else {
      return `${totalPortfoliosSelected} of ${totalPortfolios} portfolio(s) `
    }

  }

  selectAllClientPortfolios = (client) => {
    client.portfolioFees.forEach(fee => {
      fee.portfolios.forEach(portfolio => {
        portfolio.isSelected = client.isSelected;
      })
    })
  }


  convertToLocaleTime = (date: any) => {
    date = new Date(date)
    return moment(date).format('YYYY-MM-DD')

  }

  displayFeeSummary = (client) => {
    client.displayFeeSummary = !client.displayFeeSummary;
  }


  stopPropagation = ($event: angular.IAngularEvent) => {
    $event.stopPropagation();
  }

  selectPortfolio = (portfolio, client) => {
    if (!portfolio.isSelected) {
      client.isSelected = portfolio.isSelected;
    }
    else {
      let totalPortfolios = client.portfolioFees.reduce((acc, pf) => {
        return acc + pf.portfolios.length
      }, 0);

      let totalPortfoliosSelected = client.portfolioFees.reduce((acc, pf) => {
        return acc + pf.portfolios.filter(p => p.isSelected).length
      }, 0);

      client.isSelected = totalPortfolios == totalPortfoliosSelected
    }
  }

  declineInstruction = (feeInstructionId) => {
    this.decliningInstructionIds[feeInstructionId] = true;
    this.feesRepo.declineFeeInstruction(feeInstructionId)
      .then(res => {
        this.getFinalRunParameters();
        if (this.onDeclineComplete) {
          this.onDeclineComplete();
        }
      })
      .catch(err => {
        this.notifyjs("Failed to decline temp run " + extractErrorMessage(err))
      })
      .finally(() => {
        delete this.decliningInstructionIds[feeInstructionId];
      })
  }

  bulkDeclineSelected = () => {
    const selectedIds: number[] = [];
    this.clientFeeParameters.forEach(client => {
      if (!client) return;
      client.portfolioFees.forEach(pf => {
        pf.portfolios.forEach(p => {
          if (p && p.isSelected && p.feeInstructionId) {
            selectedIds.push(p.feeInstructionId);
          }
        });
      });
    });

    if (selectedIds.length === 0) return;

    this.isBulkDeclining = true;
    this.feesRepo.bulkDeclineFeeInstructions(selectedIds)
      .then(() => {
        this.getFinalRunParameters();
        if (this.onDeclineComplete) {
          this.onDeclineComplete();
        }
      })
      .catch(err => {
        this.notifyjs('Failed to bulk decline instructions ' + extractErrorMessage(err));
      })
      .finally(() => {
        this.isBulkDeclining = false;
      });
  }

  canSaveFinalRun = () => {
    let totalPortfoliosSelected = 0;

    if (this.clientFeeParameters) {
      totalPortfoliosSelected = this.clientFeeParameters.reduce((a, client) => {
        return a + client.portfolioFees.reduce((acc, pf) => {
          return acc + pf.portfolios.filter(p => p && p.isSelected).length
        }, 0);
      }, 0)
    }
    return totalPortfoliosSelected <= 0
  }


  saveFinalRun = () => {
    let feeCalculationType = this.displayMCB ? FeeCalculationType.MCB : FeeCalculationType.EDR
    let instructionsParameters = []
    this.clientFeeParameters.forEach(client => {
      if (client) {
        client.portfolioFees.forEach(pf => {
          pf.portfolios.forEach(p => {
            if (p && p.isSelected) {
              instructionsParameters.push({
                clientId: client.id,
                portfolioId: p.portfolioId,
                portfolioFeeId: pf.portfolioFeeId,
                cashAccountId: p.cashAccountId,
                feeCalculationType: feeCalculationType,
                feeDate: client.feeDate,
                id: p.feeInstructionId,
                currency: p.currency
              })
            }
          })
        })
      }
    })

    const tempScope = this.$rootScope.$new(true) as any;
    tempScope.selectedInstructions = instructionsParameters;

    this.$mdDialog.show({
      template: `<generate-instruction-sheet-dialog selected-instructions="selectedInstructions" ></generate-instruction-sheet-dialog>`,
      clickOutsideToClose: true,
      scope: tempScope
    })
      .then((result => {
        if (result) {
          this.getFinalRunParameters();
        }
      }))
  }

  formatDate(selectedDate) {
    return moment(selectedDate).endOf('month').format('YYYY-MM');
  }

  getMidRateForMonth = () => {
    if (!this.midRateDate) return;
    
    this.feesRepo
      .getMidRate(moment(this.midRateDate).endOf('month').format('YYYY-MM-DD'))
      .then((res) => {
        this.midRates = res.data as Array<any>;
      })
      .catch((err) => {
        this.notifyjs('Failed to get mid rates for selected month ' + extractErrorMessage(err));
      });
  };

  setTotalNav = () => {
    this.totalNav = this.clientFeeParameters.reduce((acc, client) => {
      if (client) {
        return acc + client.totalNavMUR
      }
    }, 0)
  }

  getSelectedNav = () => {
    let selectedNav = 0;
    if (this.clientFeeParameters.length > 0) {
      selectedNav = this.clientFeeParameters.reduce((a, client) => {
        if (client) {
          return a + client.portfolioFees.reduce((acc, pf) => {
            return acc + pf.portfolios.filter(p => p && p.isSelected).reduce((a, p) => {
              return a + p.marketValueMUR
            }, 0)
          }, 0);
        }
      }, 0)
    }
    return selectedNav;
  }


  setTotalFeeValue = () => {
    this.totalFeeValue = this.clientFeeParameters.reduce((acc, client) => {
      if (client) {
        return acc + client.totalFeeValueMUR
      }
    }, 0)
  }

  getSelectedFeeValue = () => {
    let selectedFeeValue = 0;
    if (this.clientFeeParameters.length > 0) {
      selectedFeeValue = this.clientFeeParameters.reduce((a, client) => {
        if (client) {
          return a + client.portfolioFees.reduce((acc, pf) => {
            return acc + pf.portfolios.filter(p => p && p.isSelected).reduce((a, p) => {
              // Include both regular fee and setup fee
              const regularFeeValue = p.feeValueMUR || 0;
              const setupFeeValue = this.getPortfolioSetupFeeValueMUR(p) || 0;
              return a + regularFeeValue + setupFeeValue;
            }, 0)
          }, 0);
        }
      }, 0)
    }
    return selectedFeeValue;
  }


  setTotalPortfolios = () => {
    this.totalPortfolios = this.clientFeeParameters.reduce((a, client) => {
      if (client) {
        return a + client.portfolioFees.reduce((acc, pf) => {
          return acc + pf.portfolios.filter(p => p).length
        }, 0);
      }
    }, 0)
  }

  getSelectedPortfolios = () => {
    let selectedPortfolios = 0
    if (this.clientFeeParameters.length > 0) {
      selectedPortfolios = this.clientFeeParameters.reduce((a, client) => {
        if (client) {
          return a + client.portfolioFees.reduce((acc, pf) => {
            return acc + pf.portfolios.filter(p => p && p.isSelected).length
          }, 0);
        }
      }, 0)
    }
    return selectedPortfolios;
  }

  getPortfolioSetupFeeValue = (portfolio) => {
    if (portfolio) {
      return portfolio.setupFeeValue || 0;
    }
    return 0;
  };

  getPortfolioSetupFeeValueMUR = (portfolio) => {
    if (portfolio) {
      return portfolio.setupFeeValueMUR || 0;
    }
    return 0;
  };

  hasAnySetupFees = (portfolios) => {
    if (!portfolios || portfolios.length === 0) {
      return false;
    }
    return portfolios.some((portfolio) => {
      return portfolio && (portfolio.setupFeeValue || 0) > 0;
    });
  };

  clientHasIssues = (client) => {
    if (client) {
      return client.hasIssues;
    }
    return false;
  }

  getSelectedWithIssuesCount = (): number => {
    let count = 0;
    if (!this.clientFeeParameters) return 0;
    this.clientFeeParameters.forEach(client => {
      if (!client) return;
      client.portfolioFees.forEach(pf => {
        if (!pf || !pf.portfolios) return;
        pf.portfolios.forEach(p => {
          if (p && p.isSelected && p.hasIssues) count++;
        });
      });
    });
    return count;
  }

  generateAdhocFeeInstructionSheet = (includeInstructed) => {
    let feeCalculationType = this.displayMCB ? FeeCalculationType.MCB : FeeCalculationType.EDR
    this.isLoadingParameters = true;
    let fromDate = moment(this.fromDate).endOf('month').format('YYYY-MM-DD');
    let toDate = moment(this.toDate).endOf('month').format('YYYY-MM-DD');

    this.feesRepo.generateAdhocFeeInstructionSheet(fromDate,toDate,feeCalculationType,includeInstructed)
    .then((res) => {
      let instructionExcel = res.data as any;
      this.$window.open(instructionExcel.url, '_blank');
    })
    .catch(err => {
      this.notifyjs("Failed to generate ad-hoc instruction")
    })
    .finally(() => this.isLoadingParameters = false)
  }

  generateAdhocSheet = () => {
    const tempScope = this.$rootScope.$new(true) as any;
    // Use an object to avoid AngularJS scope inheritance issues
    // When using primitives, child scopes create their own copy instead of updating parent
    tempScope.data = {
      includeInstructed: false // Default unchecked
    };
    const that = this;
    
    const dialogController = function() {
      return {
        onExport: () => {
          // Read from the object property which avoids scope inheritance issues
          const includeInstructed = tempScope.data.includeInstructed === true;
          that.$mdDialog.hide(includeInstructed);
        },
        onCancel: () => {
          that.$mdDialog.cancel();
        }
      };
    };

    this.$mdDialog.show({
      template: `
        <md-dialog flex="40">
          <reusable-modal 
            title="Export Final Run"
            accept-button-text="Export"
            cancel-button-text="Cancel"
            on-accept="$ctrl.onExport()"
            on-cancel="$ctrl.onCancel()">
            <div class="row">
              <div class="col-md-12">
                <md-checkbox ng-model="data.includeInstructed">
                  Include previously instructed values
                </md-checkbox>
              </div>
            </div>
          </reusable-modal>
        </md-dialog>
      `,
      controller: dialogController,
      controllerAs: '$ctrl',
      scope: tempScope,
      clickOutsideToClose: true
    })
    .then((includeInstructed: boolean) => {
      // Only proceed if user clicked Export (not Cancel)
      if (includeInstructed !== undefined) {
        this.generateAdhocFeeInstructionSheet(includeInstructed);
      }
    })
    .catch(() => {
      // User cancelled, do nothing
    });
  }

}

export const finalRun: angular.IComponentOptions = {
  template: require('./fees.instructions.finalrun.html'),
  controller: FinalRunController,
  bindings: {
    reloadFinalParameters: "<",
    fromDate: "<",
    toDate: "<",
    displayMCB: "<",
    threshold: "<",
    midRateDate: "<",
    midRates: "<",
    currentDate: "<",
    lastTwelveMonths: "<",
    onDeclineComplete: "&",
  }
};



// WEBPACK FOOTER //
// ./src/app/fees/instructions/finalrun/fees.instructions.finalrun.ts